import { Component, Element, Prop, State, h, Build, Host } from '@stencil/core';

const COMPONENT_NAME = 'ul-hero-header-type-1';
const CTA_ACTION = 'ui:ul-open-editor';

/**
 * Hero header component for USLegal form pages with dark navy background,
 * document preview image, a "Get form" CTA button, and an interactive
 * "Show details / Hide details" toggle that slides up short description content.
 *
 * @product uslegal
 * @source intro
 * @version 1.0.0
 * @category USLegal
 *
 * @slot heading - {required}, <me-heading variant="1" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main hero heading
 * @slot short-description - {optional}, <me-text tag="p"> - Expandable short description shown when details are open. When it carries no text the whole details block (toggle + panel) is not rendered.
 *
 * @example
 *   <ul-hero-header-type-1
 *     form-id="642325"
 *     button-text="Get form"
 *     show-details-text="Show details"
 *     hide-details-text="Hide details"
 *     image-default="https://www.pdffiller.com/preview/749/271/749271160.png"
 *     image-large="https://www.pdffiller.com/preview/749/271/749271160/large.png"
 *   >
 *     <me-heading slot="heading" variant="1" tag="h1">PA BCO-10 2023 Form</me-heading>
 *     <me-text slot="short-description" tag="p">Pennsylvania Charitable Organization Registration Statement.</me-text>
 *   </ul-hero-header-type-1>
 */
@Component({
  tag: 'ul-hero-header-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class ULHeroHeaderType1 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'uslegal' | 'inverted' = 'uslegal';

  /** Unique form identifier */
  @Prop({ attribute: 'form-id', reflect: true }) formId!: string;

  /** Form slug for editor redirect (e.g. "642325-pa-bco-10-2023") */
  @Prop({ attribute: 'data-slug', reflect: true }) slug?: string;

  /** Array of available form versions. When 2+ items are provided, clicking "Get form" opens the version picker popup instead of redirecting directly. */
  @Prop({ attribute: 'versions' }) versions?: Array<{ slug: string; label: string }> | string;

  /** @content @default Get form */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Get form';

  /** @content @default Show details */
  @Prop({ attribute: 'show-details-text' }) showDetailsText: string = 'Show details';

  /** @content @default Hide details */
  @Prop({ attribute: 'hide-details-text' }) hideDetailsText: string = 'Hide details';

  /** Background image URL for mobile viewports */
  @Prop({ attribute: 'image-default' }) imageDefault: string = '';

  /** Background image URL for desktop viewports (960px+) */
  @Prop({ attribute: 'image-large' }) imageLarge: string = '';

  @State() detailsOpen = false;

  // The hero used to measure `ul-breadcrumbs-type-1` in componentDidLoad and write
  // `min-height: calc(100dvh - <measured>px)` inline, with a ResizeObserver keeping it current.
  // That is what produced the height jump on load: the CSS shipped `calc(100dvh - 48px)`, the
  // first paint used it, and the measurement then moved the block (45px on a mobile form page,
  // where the crumb list wraps to three rows). The offset now lives in the stylesheet, so the
  // very first paint — server-rendered included — already has the final height.

  private toggleDetails = () => {
    this.detailsOpen = !this.detailsOpen;
  };

  /**
   * The heading is a second trigger for the same intent. It forwards to the button instead of
   * duplicating the flow, so the editor is opened from exactly one place — and so the button's
   * own single-flight guard covers both.
   *
   * Targets the element me-button rendered, not its host: the host carries the same class but the
   * click listener is attached to the inner element, and HTMLElement.click() on the host would
   * never reach it.
   */
  private handleHeadingClick = (event: Event) => {
    event.preventDefault();
    if (!Build.isBrowser) return;
    const cta = this.el.querySelector<HTMLElement>(`.${COMPONENT_NAME}__cta`);
    (cta?.querySelector<HTMLElement>('button') ?? cta)?.click();
  };

  // The original USLegal page hides the whole details block when the form has no description
  // worth showing (`@if($isNeedToShowDetailsBlock)` in intro.blade.php, i.e. `strlen > 15`), and
  // the exporter already applies that rule — it sends `description: null`. What reaches us is not
  // an absent node though: Herald fills every position of the schema's 2-item `children` tuple, so
  // an *empty* `me-text[slot="short-description"]` is always in the payload. Hence the check is on
  // text, not on node presence — `querySelector` alone would be truthy on every form page.
  private get hasShortDescription(): boolean {
    const nodes = Array.from(this.el.querySelectorAll('[slot="short-description"]'));
    return nodes.some(node => (node.textContent ?? '').trim().length > 0);
  }

  render() {
    const hasShortDescription = this.hasShortDescription;

    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} data-formid={this.formId}>

          {/* Background image — form preview, 20% opacity */}
          {this.imageDefault && (
            <picture class={`${COMPONENT_NAME}__background`}>
              {this.imageLarge && (
                <source media="(min-width: 960px)" srcSet={this.imageLarge} type="image/png" />
              )}
              {/*
                The form preview is the LCP element on these pages. `eager` alone only keeps it
                out of the lazy queue — it still starts at the browser's default "low" priority
                behind the scripts. `fetchpriority="high"` moves it to the front of the queue,
                which is what the head preload was supposed to do: that preload is baked from the
                template's default data, so it points at the template's example image
                (749271160) that no generated page ever renders, and it carries
                `media="(min-width: 960px)"` so it does nothing on mobile. Marking the real image
                here works regardless of the head.
              */}
              <img
                src={this.imageDefault}
                alt=""
                role="presentation"
                loading="eager"
                decoding="async"
                class={`${COMPONENT_NAME}__background-img`}
                {...({ fetchpriority: 'high' } as any)}
              />
            </picture>
          )}

          {/* Main content */}
          <div class={`${COMPONENT_NAME}__content`}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div class={`${COMPONENT_NAME}__heading js-editor-link`} data-slug={this.slug} data-send-metric="paid_form_title_button" onClick={this.handleHeadingClick} style={{ cursor: 'pointer' }}>
              <slot name="heading"></slot>
            </div>
            <me-button
              variant="primary"
              tag="button"
              class={`${COMPONENT_NAME}__cta js-editor-link`}
              data-slug={this.slug}
              data-send-metric="paid_form_get"
              triggerActions={CTA_ACTION}
              actionData={{ [CTA_ACTION]: { slug: this.slug, formId: this.formId, versions: this.versions } }}
            >
              {this.buttonText}
            </me-button>
          </div>

          {/* Details — toggle sits at the bottom, panel is abs-positioned above it */}
          {hasShortDescription && (
            <div class={`${COMPONENT_NAME}__details`}>

              {/* Toggle bar — in normal flow, always at the bottom of the hero */}
              <button
                id={`${COMPONENT_NAME}-toggle`}
                class={`${COMPONENT_NAME}__details-toggle`}
                onClick={this.toggleDetails}
                aria-expanded={this.detailsOpen ? 'true' : 'false'}
                aria-controls={`${COMPONENT_NAME}-panel`}
              >
                <span class={`${COMPONENT_NAME}__details-label`}>
                  {this.detailsOpen ? this.hideDetailsText : this.showDetailsText}
                </span>
                <me-icon
                  name="arrows--chevron-down"
                  class={`${COMPONENT_NAME}__details-icon${this.detailsOpen ? ` ${COMPONENT_NAME}__details-icon--open` : ''}`}
                />
              </button>

              {/* Panel — abs-positioned just above the toggle, slides up on open */}
              <div
                id={`${COMPONENT_NAME}-panel`}
                class={`${COMPONENT_NAME}__details-panel${this.detailsOpen ? ` ${COMPONENT_NAME}__details-panel--open` : ''}`}
                role="region"
                aria-labelledby={`${COMPONENT_NAME}-toggle`}
              >
                <div class={`${COMPONENT_NAME}__details-panel-inner`}>
                  <div class={`${COMPONENT_NAME}__details-panel-content`}>
                    <slot name="short-description"></slot>
                  </div>
                </div>
              </div>
            </div>
          )}

        </section>

        {/*
          Waiting overlay for the "Get form" flow. The CTA runs `ui:ul-open-editor`, which dispatches
          `ul-form-loader-open` and redirects ~700ms later; this loader listens for that event and
          covers the viewport (it is `position: fixed`, so nesting here is only for co-location). It
          carries no content and is not a slot, so creating it in JSX is fine.
        */}
        <ul-popup-form-loader></ul-popup-form-loader>
      </Host>
    );
  }
}
